import { getAuthToken } from "../../../auth/authClient";
import { decryptFollowupE2E, followupE2EClient } from "./followupE2EClient";

const API_BASE = (process.env.REACT_APP_MORFEUSZ_4E_API_URL || "").replace(/\/$/, "");

const dreamsUrl = (path = "") => `${API_BASE}/dreams${path}`;

const requestHeaders = (options = {}) => {
  const token = getAuthToken();
  return {
    ...(options.body ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
};

const normalizeE2EPack = (payload = {}) => {
  const e2e = payload.e2e && typeof payload.e2e === "object" ? payload.e2e : {};
  const nested = e2e.dream && typeof e2e.dream === "object" ? e2e.dream : {};
  const iv = String(e2e.iv || nested.iv || payload.dream_iv || "");
  const ct = String(e2e.ct || nested.ct || payload.dream_ct || "");
  const aad = String(e2e.aad || nested.aad || payload.dream_aad || "");

  if (!iv || !ct) return null;

  return {
    iv,
    ct,
    aad,
    dream: {
      iv,
      ct,
      aad,
    },
  };
};

const stripPlainDreamFields = (dream) => {
  if (!dream || typeof dream !== "object") return dream;
  const next = { ...dream };
  next.saveMode = "e2e";
  next.save_mode = "e2e";
  next.bodyHtml = "";
  next.excerpt = "";
  delete next.sen_opis;
  delete next.content;
  return next;
};

const hydrateFollowup = async (followup) => {
  if (!followup || typeof followup !== "object") return followup;
  return decryptFollowupE2E(followup).catch(() => followup);
};

const sanitizeResponsePayload = async (data, options = {}) => {
  if (!data || typeof data !== "object") return data;
  const next = { ...data };
  if (Array.isArray(next.dreams)) {
    next.dreams = next.dreams.map(stripPlainDreamFields);
  }
  if (next.dream && typeof next.dream === "object") {
    next.dream = stripPlainDreamFields(next.dream);
    if (options.decryptFollowup && next.dream.followup) {
      next.dream.followup = await hydrateFollowup(next.dream.followup);
    }
  }
  return next;
};

const sanitizeWritePayload = (payload = {}, { bodyRequired = false } = {}) => {
  const next = { ...(payload || {}) };
  const pack = normalizeE2EPack(next);

  if (bodyRequired && !pack) {
    const error = new Error("Sen musi być zapisany w sejfie E2E.");
    error.code = "e2e_required";
    throw error;
  }

  if (pack) {
    next.e2e = pack;
    next.dream_iv = pack.iv;
    next.dream_ct = pack.ct;
    next.dream_aad = pack.aad;
  }

  next.saveMode = "e2e";
  next.save_mode = "e2e";
  next.bodyHtml = "";
  next.sen_opis = "";
  delete next.content;
  return next;
};

const parseResponse = async (response, options = {}) => {
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.ok) {
    const error = new Error(data?.message || `dreams-http-${response.status}`);
    error.code = data?.code || `dreams-http-${response.status}`;
    error.status = response.status;
    throw error;
  }
  return sanitizeResponsePayload(data, options);
};

const request = async (path = "", options = {}) => {
  if (!API_BASE) {
    const error = new Error("dreams-api-missing");
    error.code = "dreams-api-missing";
    throw error;
  }

  const response = await fetch(dreamsUrl(path), {
    credentials: "include",
    ...options,
    headers: requestHeaders(options),
  });
  return parseResponse(response, options);
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const mergeFollowupIntoDream = (data, followup) => {
  if (!data?.dream || !followup) return data;
  return {
    ...data,
    dream: {
      ...data.dream,
      followup,
    },
  };
};

export const dreamsClient = {
  isConfigured: () => Boolean(API_BASE),
  list: () => request(""),
  get: (id) => request(`/${encodeURIComponent(id)}`, { decryptFollowup: true }),
  create: (dream) => request("", { method: "POST", body: JSON.stringify(sanitizeWritePayload(dream, { bodyRequired: true })) }),
  update: async (id, patch) => {
    const hasFollowup = hasOwn(patch, "followup");
    const followupPatch = patch?.followup;
    const followupOnly = hasFollowup && Object.keys(patch || {}).every((key) => ["followup", "saveMode", "save_mode"].includes(key));

    if (followupOnly && followupPatch && typeof followupPatch === "object") {
      const savedFollowup = await followupE2EClient.save(id, followupPatch);
      const fresh = await request(`/${encodeURIComponent(id)}`);
      return mergeFollowupIntoDream(fresh, savedFollowup.followup);
    }

    const touchesBody = Boolean(
      patch && (
        hasOwn(patch, "bodyHtml")
        || hasOwn(patch, "sen_opis")
        || hasOwn(patch, "e2e")
        || hasOwn(patch, "dream_iv")
        || hasOwn(patch, "dream_ct")
      ),
    );

    const payload = touchesBody ? sanitizeWritePayload(patch, { bodyRequired: true }) : { ...(patch || {}), saveMode: "e2e", save_mode: "e2e" };
    const data = await request(`/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(payload) });

    if (hasFollowup && followupPatch === null) {
      await followupE2EClient.remove(id).catch(() => null);
    }

    return data;
  },
  remove: (id) => request(`/${encodeURIComponent(id)}`, { method: "DELETE" }),
};
